import {
  takeLatest,
  takeEvery,
  select,
  call,
  put,
  delay,
} from 'redux-saga/effects';
import { ERROR_PASS_REQUEST, ERROR_UPDATE_REQUEST } from '@/action-types/error';
import { ANALYTICS_SEND_EVENT } from '@/action-types/analytics';
import { EXERCISE_CHANGE_LESSON_REQUEST } from '@/action-types/exercise';
import { LOADING_UPDATE_REQUEST } from '@/action-types/loading';
import {
  TRAINING_BTN_CLICK_REQUEST,
  TRAINING_BTN_CLICK_SUCCESS,
  TRAINING_SET_SCREEN_REQUEST,
  TRAINING_SET_SCREEN_SUCCESS,
} from '@/action-types/training';

import api from '@/api/exercise';
import {
  updateTrainingResult,
  setTrainingResult,
} from '@/api/training-element';
import {
  checkIsNeedReset,
  checkIsCanSave,
  convertTrainingResult,
  convertExerciseResult,
  convertTestResult,
  addTrainingAnswerId,
  convertReviewResult,
} from '@/services/book/exercise-parsers';
import { updateClassState } from '@/services/book/exercise-changes';
import { getLoading } from '@/services/book/loading-state';
import { TrainingResult } from '@/interfaces/class/training';
import {
  NEXT_EXERCISE,
  DONT_KNOW,
  NEXT_SLIDE,
  CHECK,
  CHECK_HW,
  FINISH,
  FLIP,
  CHECK_CLICK,
  SET_ACTIVITY,
  TRY_AGAIN,
  START_AGAIN,
} from '@/action-types/button';
import { REDIRECT_ACTION } from '@/action-types/redirect';

export function* trainingBtnClick(params: {
  btnType: string;
  trainingResult?: TrainingResult;
  lastActivity?: {
    lastIndexClick?: number;
    lastTextClick?: string;
    lastIsCorrect?: boolean;
    lastSlideAnswer?: number | null;
    currentScreen?: string;
  };
}) {
  const isAudioActivity = params.btnType === SET_ACTIVITY
    && 'lastActiveAudio' in (params.lastActivity || {})
    && Object.keys(params.lastActivity || {}).length <= 1;

  if (getLoading('training') && !isAudioActivity) return;
  if (getLoading('vocabulary') && !isAudioActivity) return;

  if (!isAudioActivity) {
    yield put({ type: LOADING_UPDATE_REQUEST, name: 'training', state: true });
  }

  const saveWithoutTrainings = [
    'homework-review',
    'summary',
  ];
  const saveBtns = [
    DONT_KNOW,
    NEXT_SLIDE,
    CHECK,
    CHECK_HW,
    FINISH,
    FLIP,
    NEXT_EXERCISE,
    CHECK_CLICK,
  ];
  const result = params.trainingResult;
  const { book } = yield select();

  const exerciseAlias = book.exercise?.currentExercise?.alias || '';
  const isCanSave = yield call(checkIsCanSave, book);

  let converted: any = {};

  try {
    if (
      saveBtns.indexOf(params.btnType) !== -1
      && (
        (result && result?.trainingType)
        || (saveWithoutTrainings.indexOf(exerciseAlias) !== -1)
      )) {
      yield put({
        type: ANALYTICS_SEND_EVENT,
        eventName: 'tech-check-is-can-save',
        data: {
          exerciseAlias,
          isCanSave,
          location: window.location.pathname,
        },
      });

      if (exerciseAlias === 'homework-review') {
        converted = yield call(
          convertReviewResult,
          params.trainingResult,
          book,
        );
      } else if (exerciseAlias === 'summary') {
        converted = yield call(
          convertExerciseResult,
          book,
        );
      } else if (exerciseAlias === 'lesson-test') {
        converted = yield call(
          convertTestResult,
          params.trainingResult,
          book,
          params.btnType,
        );
      } else {
        converted = yield call(
          convertTrainingResult,
          params.trainingResult,
          book,
          params.btnType,
        );
      }

      if (isCanSave && converted) {
        if (exerciseAlias === 'lesson-test') {
          if (converted.needSendResult && converted.id) {
            yield call(api.updateTestResult, converted.id, converted.testProgress);
            yield put({
              type: ANALYTICS_SEND_EVENT,
              eventName: 'tech-process-test-update',
              data: {
                id: converted.id,
                result: JSON.stringify(converted.testProgress),
              },
            });
          } else if (converted.needSendResult && !converted.id) {
            yield call(api.setTestResult, converted.testProgress);
            yield put({
              type: ANALYTICS_SEND_EVENT,
              eventName: 'tech-process-test-set',
              data: {
                id: converted.id,
                result: JSON.stringify(converted.testProgress),
              },
            });
          }
        } else if (exerciseAlias === 'homework-review') {
          if (!converted.id && params.btnType === NEXT_EXERCISE) {
            yield call(api.setExerciseResult, converted.result);
          } else if (converted.resultElement && params.btnType === CHECK_HW) {
            yield call(
              api.patchUserAnswer,
              converted.resultElement.id,
              converted.resultElement.result,
            );
          }
        } else if (exerciseAlias === 'summary') {
          if (!converted.id) {
            yield call(api.setExerciseResult, converted.result);

            // NOTE exception for summary
            book.exercise.currentExercise = {
              ...(book.exercise.currentExercise || {}),
              exerciseProgress: [{ id: 'filled' }],
            };
          }
        } else if (converted.id) {
          yield call(updateTrainingResult, converted.id, converted.resultElement);
          yield put({
            type: ANALYTICS_SEND_EVENT,
            eventName: 'tech-process-training-update',
            data: {
              id: converted.id,
              result: JSON.stringify(converted.resultElement),
            },
          });
        } else {
          let response;

          try {
            response = yield call(setTrainingResult, converted.resultElement);
            yield put({
              type: ANALYTICS_SEND_EVENT,
              eventName: 'tech-process-training-set',
              data: {
                id: converted.id,
                result: JSON.stringify(converted.resultElement),
              },
            });
          } catch (e) {
            yield put({
              type: ANALYTICS_SEND_EVENT,
              eventName: 'tech-process-training-save-error',
              data: {
                id: converted.id,
                message: e?.message,
              },
            });
          }

          if (response?.data?.trainingResultSet?.id) {
            converted.state = yield call(addTrainingAnswerId, {
              id: response.data?.trainingResultSet?.id,
              state: converted.state,
              trainingType: converted.resultElement.trainingType,
            });
          }
        }
      }
    }

    if (params.btnType === TRY_AGAIN || params.btnType === START_AGAIN) {
      if (book.route.lessonId && book.route.exerciseId) {
        yield put({
          type: EXERCISE_CHANGE_LESSON_REQUEST,
          data: {
            lessonId: book.route.lessonId,
            exerciseId: book.route.exerciseId,
          },
        });
      }
    }

    if (params.btnType !== TRY_AGAIN && params.btnType !== START_AGAIN) {
      yield put({
        type: TRAINING_BTN_CLICK_SUCCESS,
        data: {
          ...(book.exercise || {}),
          ...(converted.state?.exercise || {}),
          currentExercise: {
            ...(book.exercise.currentExercise || {}),
            ...(converted?.state?.exercise?.currentExercise || {}),
            ...(params.lastActivity || {}),
          },
        },
        needSync: !isAudioActivity,
      });

      if (checkIsNeedReset(params.btnType)) yield delay(500);

      const updatedState = yield call(
        updateClassState,
        book,
        params.btnType,
        result,
        params.lastActivity,
      );

      yield put({
        type: TRAINING_BTN_CLICK_SUCCESS,
        data: {
          ...(book.exercise || {}),
          ...(converted.state?.exercise || {}),
          ...(updatedState.exercise || {}),
          currentExercise: {
            ...(book.exercise.currentExercise || {}),
            ...(converted.state?.exercise?.currentExercise || {}),
            ...(updatedState.exercise.currentExercise || {}),
            ...(result ? { answers: result } : {}),
            lastIndexClick: null,
            lastIsCorrect: null,
            lastTextClick: null,
          },
        },
        needSync: !isAudioActivity,
      });
    }

    if (params.btnType === NEXT_EXERCISE) {
      const exerciseId = book.exercise.currentExercise?.nextExercise?.id;

      if (book.route.lessonId && exerciseId) {
        const {
          sessionId, courseId, lessonId,
        } = book.route;
        const path = `/class/${sessionId}/${courseId}/${lessonId}/${exerciseId}/`;

        yield put({
          type: REDIRECT_ACTION,
          payload: {
            path,
            withNativeRedirect: true,
          },
        });
      }
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'trainingBtnClick'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
    yield put({
      type: ANALYTICS_SEND_EVENT,
      eventName: 'tech-process-btn-click-error',
      data: {
        id: converted.id,
        message: e?.message,
      },
    });
  } finally {
    if (!isAudioActivity) {
      yield put({ type: LOADING_UPDATE_REQUEST, name: 'training', state: false });
    }
  }
}

export function* trainingSetScreen(params: {
  screen: string;
}) {
  const state = yield select();

  yield put({
    type: TRAINING_SET_SCREEN_SUCCESS,
    data: {
      exercise: {
        ...state.book.exercise,
        currentExercise: {
          ...state.book.exercise.currentExercise,
          currentScreen: params.screen,
        },
      },
    },
  });
}

export default function* trainingSaga() {
  yield takeEvery<{ type: string; } & any>(TRAINING_BTN_CLICK_REQUEST, trainingBtnClick);
  yield takeLatest<{ type: string; } & any>(TRAINING_SET_SCREEN_REQUEST, trainingSetScreen);
}
